// client/src/coms/conversations/ConversationList.js
// Rows for Chats and the quick switcher: avatar or server icon, name, where it is, preview, unread and mentions.
import React, { useState } from 'react';

function initials(s) {
  const parts = String(s || '?').trim().split(/\s+/).filter(Boolean);
  return (parts.length > 1 ? parts[0][0] + parts[1][0] : String(s || '?').slice(0, 2)).toUpperCase();
}

function Badge({ item }) {
  if (item.mentions > 0) return <span className="vx-conv-mention" aria-label={`${item.mentions} mention${item.mentions === 1 ? '' : 's'}`}>{item.mentions > 99 ? '99+' : item.mentions}</span>;
  if (item.unread) return <span className="vx-conv-dot" aria-label="Unread" />;
  return null;
}

function Icon({ item }) {
  const [broken, setBroken] = useState(false);
  const src = item.kind === 'dm' ? item.avatarUrl : item.serverLogo;
  const label = item.kind === 'dm' ? item.title : item.serverName;
  return (
    <span className={`vx-conv-icon${item.kind === 'dm' ? ' is-round' : ''}`} aria-hidden="true">
      {/* Initials underneath; the picture covers them only once it has actually loaded. */}
      <span>{initials(label)}</span>
      {src && !broken ? <img src={src} alt="" onError={() => setBroken(true)} onLoad={(e) => e.currentTarget.classList.add('is-loaded')} loading="lazy" /> : null}
      {item.kind === 'channel' ? <span className="vx-conv-hash">#</span> : null}
    </span>
  );
}

function ago(ms) {
  if (!ms) return '';
  const s = Math.max(0, (Date.now() - ms) / 1000);
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)}d`;
  return new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

export function ConversationRow({ item, onOpen, onPin, pinned, active, focused, rowId }) {
  return (
    <li className={`vx-conv-row${item.unread || item.mentions ? ' is-unread' : ''}${active ? ' is-active' : ''}${focused ? ' is-focused' : ''}`} id={rowId} role="option" aria-selected={!!focused}>
      <button type="button" className="vx-conv-main" onClick={() => onOpen(item)} data-testid={`conv-${item.id}`} data-conv={item.id}>
        <Icon item={item} />
        <span className="vx-conv-text">
          <span className="vx-conv-title">
            {item.kind === 'channel' ? <span className="vx-conv-prefix">#</span> : null}
            {item.title}
          </span>
          <span className="vx-conv-sub">{item.preview ? item.preview : item.kind === 'channel' ? item.serverName : 'Direct message'}</span>
        </span>
        <span className="vx-conv-meta">
          <span className="vx-conv-time">{ago(item.at)}</span>
          <Badge item={item} />
        </span>
      </button>
      {onPin ? (
        <button type="button" className={`vx-conv-pin${pinned ? ' is-on' : ''}`} aria-pressed={!!pinned} aria-label={pinned ? `Unpin ${item.title}` : `Pin ${item.title}`} onClick={() => onPin(item, !pinned)}>
          {pinned ? '★' : '☆'}
        </button>
      ) : null}
    </li>
  );
}

export default function ConversationSections({ sections, onOpen, onPin, pinnedIds, activeId, focusedId, idPrefix = 'vx-conv' }) {
  return (
    <>
      {sections.map(({ key, title, items, empty }) =>
        items.length || empty ? (
          <section key={key} className="vx-conv-section" aria-label={title}>
            <h3 className="vx-conv-heading">{title}</h3>
            {items.length ? (
              <ul className="vx-conv-list" role="listbox" aria-label={title}>
                {items.map((it) => (
                  <ConversationRow
                    key={it.id}
                    item={it}
                    onOpen={onOpen}
                    onPin={onPin}
                    pinned={pinnedIds && pinnedIds.has(it.id)}
                    active={activeId === it.id}
                    focused={focusedId === it.id}
                    rowId={`${idPrefix}-${it.id}`}
                  />
                ))}
              </ul>
            ) : (
              <p className="vx-conv-empty">{empty}</p>
            )}
          </section>
        ) : null
      )}
    </>
  );
}
